import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { combineFiles, DataAppendSummary } from '../../api/automationApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload two or more files (.xlsx, .xls or .csv) that all have the same columns.',
  'Choose which sheet to read from the Excel files and any clean-up options.',
  'Click Fuse Intel Files. The combined workbook downloads automatically.',
];

type SheetMode = 'first' | 'name' | 'index';

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

const selectClass =
  'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground';

function IntelFusionPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [sheetMode, setSheetMode] = useState<SheetMode>('first');
  const [sheetName, setSheetName] = useState('');
  const [sheetIndex, setSheetIndex] = useState('0');
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [dropColAOnly, setDropColAOnly] = useState(false);
  const [outputFilename, setOutputFilename] = useState('combined_output.xlsx');
  const { status, error, result, fail, run } = useFileRun<FileResult<DataAppendSummary>>();

  const resolveSheet = (): string | number => {
    if (sheetMode === 'name' && sheetName.trim()) return sheetName.trim();
    if (sheetMode === 'index') return Math.max(0, parseInt(sheetIndex, 10) || 0);
    return 0;
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload at least one file before combining.');
      return;
    }
    await run(() => combineFiles(files, {
      sheetName: resolveSheet(),
      removeDuplicates,
      dropColAOnly,
      outputFilename,
    }), 'Combining files failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <FileDrop
        label="Files to combine"
        note="All files must have exactly the same column names and order."
        required
        multiple
        accept={mission.accept + ',.xls'}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <div className="mb-4 grid gap-4 rounded-lg border border-border bg-muted p-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="if-sheet" className="mb-1.5">
            Sheet to read from Excel files
          </Label>
          <select
            id="if-sheet"
            className={selectClass}
            value={sheetMode}
            onChange={(e) => setSheetMode(e.target.value as SheetMode)}
          >
            <option value="first">First sheet (default)</option>
            <option value="name">Sheet by name</option>
            <option value="index">Sheet by index</option>
          </select>
          {sheetMode === 'name' && (
            <Input
              className="mt-2"
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              placeholder="Sheet name"
              aria-label="Sheet name"
            />
          )}
          {sheetMode === 'index' && (
            <Input
              className="mt-2"
              type="number"
              min={0}
              value={sheetIndex}
              onChange={(e) => setSheetIndex(e.target.value)}
              aria-label="Sheet index (0-based)"
            />
          )}
        </div>
        <div className="space-y-3 text-sm text-foreground">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={removeDuplicates}
              onChange={(e) => setRemoveDuplicates(e.target.checked)}
            />
            <span>
              Remove duplicate rows
              <span className="block text-xs text-muted-foreground">
                Drops exact duplicate rows from the combined output.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={dropColAOnly}
              onChange={(e) => setDropColAOnly(e.target.checked)}
            />
            <span>
              Drop metadata/footer rows
              <span className="block text-xs text-muted-foreground">
                Removes rows whose column A starts with a known label (Bank Pan No, Merchant Pan No,
                Curr Ac GSTN, Acc No2 GSTN, UBS GSTN, EEFC GSTN).
              </span>
            </span>
          </label>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="if-output" className="mb-1.5">
          Output filename
        </Label>
        <Input
          id="if-output"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="combined_output.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Fusing…' : 'Fuse Intel Files'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Fusing…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          {result.summary && (
            <div className="mt-2 text-xs text-green-800">
              <p>
                {result.summary.files_processed} file(s) · {result.summary.rows_appended.toLocaleString()} rows
                {result.summary.empty_files.length > 0 &&
                  ` · ${result.summary.empty_files.length} empty file(s) skipped`}
              </p>
              <ul className="mt-1 list-disc pl-5">
                {Object.entries(result.summary.file_row_counts).map(([name, count]) => (
                  <li key={name}>
                    {name}: {count.toLocaleString()} rows
                  </li>
                ))}
              </ul>
            </div>
          )}
        </SuccessNotice>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload your files above to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default IntelFusionPanel;
